import { Bar, card, Eyebrow, ghostButton, primaryButton, Ring, Screen, Title } from '../components/ui';
import { getTopic, TOPICS } from '../content/topics';
import { BADGE_BY_ID } from '../engine/progress';
import { nextTopic, topicPercent } from '../store/state';
import { useApp, useStartSet } from '../store/useApp';

export function Home() {
  const { state, dispatch } = useApp();
  const startSet = useStartSet();
  const { progress, settings } = state;
  const suggestion = getTopic(nextTopic(progress));
  const benchBadge = state.benchBadgeId ? BADGE_BY_ID.get(state.benchBadgeId) : null;

  const warm = TOPICS.filter((t) => (progress.best[t.id] ?? 0) > 0).slice(0, 2);
  const level = 1 + Math.floor(progress.xp / 250);

  return (
    <Screen>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Eyebrow>Shop Math</Eyebrow>
          <Title>Pick one thing.</Title>
        </div>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 7,
            padding: '8px 12px',
            borderRadius: 999,
            background: 'var(--surface-2)',
            border: '1px solid var(--line-strong)',
          }}
        >
          <div style={{ width: 7, height: 7, borderRadius: 999, background: 'var(--amber)' }} />
          <div className="num" style={{ font: '700 13px/1 var(--font-display)' }}>
            {progress.streak}
          </div>
          <div style={{ font: '500 11px/1 var(--font-body)', color: 'var(--text-4)' }}>day run</div>
        </div>
      </div>

      <div style={{ ...card, gap: 13 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 10 }}>
          <div style={{ font: '400 14px/1.4 var(--font-body)', color: 'var(--text-3)' }}>
            {progress.solved === 0
              ? 'Nothing worked yet. That is fine — start anywhere.'
              : `${progress.solved} problems worked so far`}
          </div>
          <div
            className="num"
            style={{ font: '700 12px/1 var(--font-display)', color: 'var(--lime)' }}
          >
            {progress.xp} XP
          </div>
        </div>
        <Bar pct={((progress.xp % 250) / 250) * 100} />
        <div style={{ font: '400 12px/1.45 var(--font-body)', color: 'var(--text-4)', textWrap: 'pretty' }}>
          Level {level} · longest run {progress.longest} days · {progress.freezes} skip days banked.
          Miss a day and one covers you, and every badge you have earned stays yours either way.
        </div>
      </div>

      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 13,
          padding: 18,
          borderRadius: 'var(--radius-lg)',
          background: 'linear-gradient(155deg,#232b23,#1a1f24 62%)',
          border: '1px solid oklch(0.82 0.17 130 / .22)',
        }}
      >
        <Eyebrow tone="var(--lime)">Up next</Eyebrow>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
          <div style={{ font: '700 25px/1.1 var(--font-display)', letterSpacing: '-.02em' }}>
            {suggestion.name}
          </div>
          <div
            style={{ font: '400 14px/1.45 var(--font-body)', color: 'var(--text-3)', textWrap: 'pretty' }}
          >
            {suggestion.blurb}
          </div>
        </div>
        <button
          type="button"
          style={primaryButton}
          onClick={() => startSet(suggestion.id)}
        >
          Start {settings.setLength} questions
        </button>
        <div style={{ font: '400 12px/1.4 var(--font-body)', color: 'var(--text-4)' }}>
          No timer. Park anything that gets sticky.
        </div>
      </div>

      <div
        style={{
          ...card,
          gap: 11,
          border: '1px dashed oklch(0.82 0.17 65 / .4)',
        }}
      >
        <Eyebrow tone="var(--amber)">At the bench</Eyebrow>
        <div
          style={{ font: '400 14.5px/1.5 var(--font-body)', color: 'var(--text-2)', textWrap: 'pretty' }}
        >
          {suggestion.bench}
        </div>
        <button
          type="button"
          onClick={() => dispatch({ type: 'logBench' })}
          style={{
            ...ghostButton,
            width: '100%',
            flex: 'none',
            borderColor: 'oklch(0.82 0.17 65 / .5)',
            color: 'var(--amber)',
          }}
        >
          {progress.bench > 0 ? `Logged. ${progress.bench} done` : 'I did it at the bench'}
        </button>
        {/* The prototype computed this badge and then threw it away. */}
        {benchBadge && (
          <div
            role="status"
            style={{
              font: '600 12.5px/1.4 var(--font-body)',
              color: 'var(--amber)',
              textWrap: 'pretty',
            }}
          >
            Badge earned: {benchBadge.name}. Yours for good.
          </div>
        )}
      </div>

      {warm.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
          <Eyebrow>Keep warm</Eyebrow>
          {warm.map((topic) => {
            const pct = topicPercent(progress, topic.id, settings.setLength);
            return (
              <button
                key={topic.id}
                type="button"
                onClick={() => startSet(topic.id)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 12,
                  width: '100%',
                  minHeight: 64,
                  padding: '13px 15px',
                  borderRadius: 14,
                  background: 'var(--surface)',
                  border: '1px solid var(--line)',
                  cursor: 'pointer',
                  color: 'var(--text)',
                }}
              >
                <div style={{ display: 'flex', flexDirection: 'column', gap: 3, textAlign: 'left' }}>
                  <div style={{ font: '600 15px/1.2 var(--font-display)' }}>{topic.name}</div>
                  <div style={{ font: '400 12px/1.3 var(--font-body)', color: 'var(--text-4)' }}>
                    Best run: {progress.best[topic.id] ?? 0} right
                  </div>
                </div>
                <Ring pct={pct} />
              </button>
            );
          })}
        </div>
      )}
    </Screen>
  );
}
